import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  Menu,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCopy,
  IconHistory,
  IconMessagePlus,
  IconSend,
  IconTrash,
} from "@tabler/icons-react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import {
  sendChatMessage,
  getChatConversations,
  getChatConversation,
  deleteChatConversation,
  getChatSuggestions,
} from "../api";
import AssistantReferences from "./AssistantReferences";

function usePageContext() {
  const [location] = useLocation();
  return useMemo(() => {
    const parts = location.split("/").filter(Boolean);
    const ctx = { page: parts[0] || null };
    if (parts[0] === "projects" && parts[1]) ctx.projectId = parts[1];
    if (parts[0] === "candidates" && parts[1]) ctx.candidateId = parts[1];
    return ctx;
  }, [location]);
}

export default function ChatDrawer({ opened, onClose }) {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const pageContext = usePageContext();

  useEffect(() => {
    if (opened) {
      loadConversations();
      loadSuggestions();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getChatConversations();
      setConversations(data || []);
    } catch {}
  };

  const loadSuggestions = async () => {
    try {
      const data = await getChatSuggestions();
      setSuggestions(data || []);
    } catch {}
  };

  const loadConversation = async (id) => {
    try {
      const data = await getChatConversation(id);
      setActiveConversation(data);
      setMessages(data.messages || []);
    } catch {}
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setInput("");
    inputRef.current?.focus();
  };

  const deleteConversation = async (id, e) => {
    e?.stopPropagation();
    try {
      await deleteChatConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversation?.id === id) startNewConversation();
    } catch {}
  };

  const sendMessage = useCallback(
    async (text) => {
      const messageText = text || input;
      if (!messageText.trim() || loading) return;

      setInput("");
      const userMsg = {
        role: "user",
        content: messageText,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);

      try {
        const data = await sendChatMessage(
          messageText,
          activeConversation?.id || undefined,
          pageContext,
        );

        const assistantMsg = {
          role: "assistant",
          content: data.message,
          references: data.references ?? [],
          moreCandidates: data.moreCandidates ?? 0,
          moreProjects: data.moreProjects ?? 0,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (!activeConversation) {
          setActiveConversation({
            id: data.conversationId,
            title: messageText.slice(0, 80),
          });
          loadConversations();
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: t("assistant_error"),
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [input, loading, activeConversation, pageContext, t],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = (content) => navigator.clipboard.writeText(content);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(420px, 100vw)"
      title={
        <Group gap="xs">
          <Text fw={600}>{t("assistant_title")}</Text>
          <Menu shadow="md" width={260} position="bottom-start">
            <Menu.Target>
              <Tooltip label={t("assistant_history")}>
                <ActionIcon variant="subtle" size="sm">
                  <IconHistory size={16} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconMessagePlus size={14} />}
                onClick={startNewConversation}
              >
                {t("assistant_new_conversation")}
              </Menu.Item>
              {conversations.length > 0 && <Menu.Divider />}
              <ScrollArea.Autosize mah={300}>
                {conversations.map((c) => (
                  <Menu.Item
                    key={c.id}
                    onClick={() => loadConversation(c.id)}
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={(e) => deleteConversation(c.id, e)}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    }
                  >
                    <Text size="xs" lineClamp={1}>
                      {c.title || t("assistant_untitled")}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t("assistant_messages", { count: c.messageCount ?? 0 })}
                    </Text>
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        </Group>
      }
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
          padding: 0,
        },
      }}
    >
      {/* Messages */}
      <ScrollArea
        style={{ flex: 1, minHeight: 0 }}
        viewportRef={scrollRef}
        px="md"
      >
        <Stack gap="md" py="md">
          {messages.length === 0 && !loading && (
            <Stack align="center" py="xl" gap="md">
              <Text size="sm" c="dimmed" ta="center">
                {t("assistant_empty_prompt")}
              </Text>
              {suggestions.length > 0 && (
                <Stack gap="xs" w="100%">
                  {suggestions.map((s, i) => (
                    <Button
                      key={i}
                      variant="light"
                      size="xs"
                      fullWidth
                      justify="flex-start"
                      onClick={() => sendMessage(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              message={msg}
              onCopy={copyMessage}
              onNavigate={(path) => { onClose(); setLocation(path); }}
            />
          ))}

          {loading && (
            <Group gap="xs" px="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                {t("assistant_thinking")}
              </Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      {/* Input */}
      <Box
        px="md"
        py="sm"
        style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
      >
        <Group align="flex-end" gap="xs">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("assistant_input_placeholder")}
            autosize
            minRows={1}
            maxRows={4}
            style={{ flex: 1 }}
            disabled={loading}
          />
          <ActionIcon
            size="lg"
            variant="filled"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      </Box>
    </Drawer>
  );
}

function MessageBubble({ message, onCopy, onNavigate }) {
  const { t } = useTranslation("Veft");
  const isUser = message.role === "user";

  return (
    <Box
      px="sm"
      py="xs"
      style={(theme) => ({
        backgroundColor: isUser ? theme.colors.blue[0] : undefined,
        borderRadius: theme.radius.md,
      })}
    >
      <Group justify="space-between" mb={2}>
        <Text size="xs" fw={600} c={isUser ? "blue" : "dimmed"}>
          {isUser ? t("assistant_you") : t("assistant_title")}
        </Text>
        {!isUser && (
          <Tooltip label={t("assistant_copy")}>
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => onCopy(message.content)}
            >
              <IconCopy size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      <Text
        size="sm"
        style={{ whiteSpace: "pre-wrap" }}
        dangerouslySetInnerHTML={
          !isUser ? { __html: formatMarkdown(message.content) } : undefined
        }
      >
        {isUser ? message.content : undefined}
      </Text>

      {!isUser && (
        <AssistantReferences
          references={message.references}
          moreCandidates={message.moreCandidates}
          moreProjects={message.moreProjects}
          onNavigate={onNavigate}
        />
      )}
    </Box>
  );
}

function formatMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(
      /`(.*?)`/g,
      '<code style="background:#f1f3f5;padding:2px 4px;border-radius:3px">$1</code>',
    )
    .replace(/\n/g, "<br/>");
}
